import React, { useMemo } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  Box,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  EditRounded as EditIcon,
  DeleteOutlineRounded as DeleteIcon,
} from '@mui/icons-material';

import {
  getTableStyles,
  getRowStyles,
  initialTableState,
} from '../../../utils/mabScrapingTableConfig';

function KeywordsCell({ keywords }) {
  const list = Array.isArray(keywords) ? keywords : [];
  if (list.length === 0) return <Chip size="small" label="0" />;
  const preview = list.slice(0, 10).join(', ') + (list.length > 10 ? '…' : '');
  return (
    <Tooltip title={preview} placement="top" arrow>
      <Chip size="small" label={list.length} />
    </Tooltip>
  );
}

export default function TwitterAccountsTable({
  rows,
  loading,
  onEdit,
  onDelete,
  onToggleActive,
}) {
  const theme = useTheme();

  const columns = useMemo(
    () => [
      {
        accessorKey: 'username',
        header: 'Username',
        Cell: ({ cell }) => `@${cell.getValue()}`,
        size: 140,
      },
      {
        accessorKey: 'display_name',
        header: 'Display name',
        size: 200,
      },
      {
        accessorKey: 'keywords',
        header: 'Keywords',
        size: 110,
        Cell: ({ cell }) => <KeywordsCell keywords={cell.getValue()} />,
        sortingFn: (a, b) =>
          (a.original.keywords?.length || 0) - (b.original.keywords?.length || 0),
      },
      {
        accessorKey: 'match_mode',
        header: 'Match mode',
        size: 140,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            label={cell.getValue() === 'substring' ? 'Substring' : 'Word boundary'}
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: 'max_results',
        header: 'Max',
        size: 80,
        Cell: ({ cell }) => cell.getValue() ?? '—',
      },
      {
        accessorKey: 'active',
        header: 'Active',
        size: 90,
        Cell: ({ row }) => (
          <Switch
            size="small"
            checked={Boolean(row.original.active)}
            onChange={(e) => onToggleActive(row.original, e.target.checked)}
          />
        ),
      },
    ],
    [onToggleActive],
  );

  const tableStyles = useMemo(() => getTableStyles(theme), [theme]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    getRowId: (row) => row.username,
    state: { isLoading: loading },
    initialState: initialTableState,
    enableStickyHeader: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableKeyboardShortcuts: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => onDelete(row.original)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableBodyRowProps: ({ row }) => getRowStyles(theme, false, row),
    ...tableStyles,
  });

  return <MaterialReactTable table={table} />;
}
